/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { createSelector } from "reselect";
import { RootState } from "..";
import { selectSearchFilterDetails } from "../selectors";

export const selectMachineProcessUpdates = (state: RootState) =>
  state.processUpdates.groupedTableData.machine;

export const selectJobProcessUpdates = (state: RootState) =>
  state.processUpdates.groupedTableData.job;

export const selectCharacteristicProcessUpdates = (state: RootState) =>
  state.processUpdates.groupedTableData.characteristic;

/** Unique job names seen in Process Updates data, since this page's job names aren't
 * necessarily present in the Job Performance page's `jobTypes` (which only load on that route) */
export const selectProcessUpdatesJobNameOptions = createSelector(
  [selectCharacteristicProcessUpdates, selectSearchFilterDetails],
  (characteristicUpdates, filterDetails) => {
    const uniqueNames = Array.from(
      new Set(
        Object.values(characteristicUpdates.processUpdatesPerf)
          .map((item) => item.jobName)
          .filter((name): name is string => !!name)
      )
    ).sort((a, b) => a.localeCompare(b));

    return filterDetails.value
      ? uniqueNames.filter((name) =>
          name.toLowerCase().includes(filterDetails.value.toLowerCase())
        )
      : uniqueNames;
  }
);

export const selectCharacteristicExpandCollapseState = (state: RootState) =>
  state.processUpdates.expandCollapseState.characteristic;

export const selectJobExpandCollapseState = (state: RootState) =>
  state.processUpdates.expandCollapseState.job;

export const selectMachineExpandCollapseState = (state: RootState) =>
  state.processUpdates.expandCollapseState.machine;

export const selectJobDetailViewState = (state: RootState) =>
  state.processUpdates.expandCollapseState.job.openDetailsList;

export const selectMachineDetailViewState = (state: RootState) =>
  state.processUpdates.expandCollapseState.machine.openDetailsList;

export const selectCharacteristicDetailViewState = (state: RootState) =>
  state.processUpdates.expandCollapseState.characteristic.openDetailsList;

export const selectLastFetchedForRefresh = (s: RootState) =>
  s.processUpdates.lastFetchedForRefresh;

export const selectApplyingOffset = (state: RootState) =>
  state.processUpdates.applyingOffsetKeys;

export const selectIsOffsetApplying = (key: string) => (state: RootState) =>
  state.processUpdates.applyingOffsetKeys.includes(key);
